export interface Point {
  x: number
  y: number
}

/**
 * A node's bounding-box representation for the router.
 *
 * The router needs the node's VISUAL CENTER (for computing where pipes
 * attach) and the half-width/half-height (for computing where the
 * handles sit at the node's edges). Callers build a NodeBox from a
 * vertex's top-left `diagram.position` plus its type-specific
 * dimensions via `getNodeDimensions()`.
 *
 * See `vertexToRouteBox` in `grid-snap.ts` for the conversion helper.
 */
export interface NodeBox {
  /** Visual center of the node in flow coordinates. */
  center: Point
  /** Half the node's rendered width in px. */
  halfWidth: number
  /** Half the node's rendered height in px. */
  halfHeight: number
}

export interface RouteResult {
  points: Point[]
  sourceHandle: string
  targetHandle: string
}

export type Side = 'right' | 'left' | 'bottom' | 'top'

/**
 * Optional constraints the caller can pass to route around existing pipes.
 *
 * When routing a new pipe from a multi-connection node like a tee, the
 * caller supplies the set of sides already in use so the router avoids
 * them. Without this, the router always picks the dominant-axis side and
 * every pipe from the same tee visually exits the same point — BUG-007
 * from Dominik's QA Round 2 (2026-04-14).
 */
export interface RouteOptions {
  takenSrcSides?: ReadonlySet<Side>
  takenTgtSides?: ReadonlySet<Side>
  /** Pin the source end to this side, overriding the auto pick + collision avoidance. */
  forcedSrcSide?: Side
  /** Pin the target end to this side, overriding the auto pick + collision avoidance. */
  forcedTgtSide?: Side
  /** Ordered corner chain the route must pass through, kept verbatim.
   *  Pairwise-orthogonal. The router computes only the two end legs. */
  waypoints?: Point[]
}

/** Distance past a handle before the pipe makes its first bend. */
export const MARGIN = 30

/** Minimum positive outward stub so an end always clears its own box. */
const CLEAR = 1

/**
 * Return the first side from {preferred, 90°CW, 180°, 270°} that isn't
 * in the taken set. If every side is taken, fall back to the preferred
 * side (lesser evil — keeps deterministic output instead of throwing).
 */
function pickNonCollidingSide(preferred: Side, taken: ReadonlySet<Side> | undefined): Side {
  if (!taken || !taken.has(preferred)) return preferred
  // Rotation order starting from preferred: right → bottom → left → top → right
  const order: Side[] = ['right', 'bottom', 'left', 'top']
  const start = order.indexOf(preferred)
  for (let i = 1; i < order.length; i++) {
    const candidate = order[(start + i) % order.length]!
    if (!taken.has(candidate)) return candidate
  }
  return preferred
}

/**
 * Compute an orthogonal (horizontal/vertical) routing path between two
 * nodes. Returns intermediate waypoints and the canonical React Flow
 * handle IDs for the source and target.
 *
 * The router picks the handle side on each node based on which axis
 * has the larger delta (horizontal-dominant → left/right handles,
 * vertical-dominant → top/bottom), then emits an L or Z-shaped route
 * depending on the combination.
 *
 * **Important**: uses each node's actual half-dimensions rather than a
 * hardcoded constant. Prior to 2026-04-09 this function assumed every
 * node was 60×60, which produced visible pipe jogs on any diagram with
 * mixed-size devices (Dominik's QA case).
 */
export function computeOrthogonalRoute(
  src: NodeBox,
  tgt: NodeBox,
  options?: RouteOptions,
): RouteResult {
  // Waypoint routing is checked FIRST — before the forced-sides early return
  // and the auto direct/L/Z path — and handles pinned and unpinned ends
  // uniformly (pins only fix the side).
  if (options?.waypoints && options.waypoints.length > 0) {
    return routeThroughWaypoints(src, tgt, options.waypoints, options)
  }

  const dx = tgt.center.x - src.center.x
  const dy = tgt.center.y - src.center.y
  const absDx = Math.abs(dx)
  const absDy = Math.abs(dy)

  // 1. Determine preferred handle sides based on dominant axis
  let srcSide: Side
  let tgtSide: Side

  if (absDy > absDx) {
    // Vertical dominant
    if (dy >= 0) {
      srcSide = 'bottom'
      tgtSide = 'top'
    } else {
      srcSide = 'top'
      tgtSide = 'bottom'
    }
  } else {
    // Horizontal dominant (or equal)
    if (dx >= 0) {
      srcSide = 'right'
      tgtSide = 'left'
    } else {
      srcSide = 'left'
      tgtSide = 'right'
    }
  }

  // 1b. Avoid sides already occupied by other edges on either node.
  // BUG-007 regression: without this, every pipe from a tee with multiple
  // outlets got the same dominant-axis side and visually overlapped.
  srcSide = options?.forcedSrcSide ?? pickNonCollidingSide(srcSide, options?.takenSrcSides)
  tgtSide = options?.forcedTgtSide ?? pickNonCollidingSide(tgtSide, options?.takenTgtSides)

  const sourceHandle = `${srcSide}-src`
  const targetHandle = `${tgtSide}-tgt`

  // 2. Compute the actual edge positions of each handle — uses the
  //    type-specific half-dimensions so different-size nodes produce
  //    handles at their real edges.
  const srcHandle = handleEdgePosition(src, srcSide)
  const tgtHandle = handleEdgePosition(tgt, tgtSide)

  if (options?.forcedSrcSide || options?.forcedTgtSide) {
    return {
      points: routeSidesAvoidingBoxes(src, tgt, srcHandle, tgtHandle, srcSide, tgtSide),
      sourceHandle,
      targetHandle,
    }
  }

  // 3. Check for direct connection: handles face each other and are
  //    aligned within 2px on the perpendicular axis.
  const facingH = (srcSide === 'right' && tgtSide === 'left') || (srcSide === 'left' && tgtSide === 'right')
  const facingV = (srcSide === 'bottom' && tgtSide === 'top') || (srcSide === 'top' && tgtSide === 'bottom')

  if (facingH && Math.abs(srcHandle.y - tgtHandle.y) <= 2) {
    return { points: [], sourceHandle, targetHandle }
  }
  if (facingV && Math.abs(srcHandle.x - tgtHandle.x) <= 2) {
    return { points: [], sourceHandle, targetHandle }
  }

  // 4. Route with waypoints. Compute exit point from source handle
  //    by MARGIN, then decide L-shape vs Z-shape based on axis pairings.
  const srcExit = exitPoint(srcHandle, srcSide)

  // Determine entry side of target (opposite of tgtSide)
  const tgtEntrySide = oppositeSide(tgtSide)
  const tgtEntry = exitPoint(tgtHandle, tgtEntrySide)

  const srcHorizontal = srcSide === 'left' || srcSide === 'right'
  const tgtHorizontal = tgtSide === 'left' || tgtSide === 'right'

  let waypoints: Point[]

  if (srcHorizontal && !tgtHorizontal) {
    // Source exits horizontally, target enters vertically: L-shape
    waypoints = [
      { x: tgtEntry.x, y: srcExit.y },
    ]
  } else if (!srcHorizontal && tgtHorizontal) {
    // Source exits vertically, target enters horizontally: L-shape
    waypoints = [
      { x: srcExit.x, y: tgtEntry.y },
    ]
  } else if (srcHorizontal && tgtHorizontal) {
    // Both horizontal: Z-shape via midpoint
    const midX = (srcExit.x + tgtEntry.x) / 2
    waypoints = [
      { x: midX, y: srcExit.y },
      { x: midX, y: tgtEntry.y },
    ]
  } else {
    // Both vertical: Z-shape via midpoint
    const midY = (srcExit.y + tgtEntry.y) / 2
    waypoints = [
      { x: srcExit.x, y: midY },
      { x: tgtEntry.x, y: midY },
    ]
  }

  // Anti-fold guard (#502, B33 part c). The naive L/Z above is emitted without
  // any obstacle check — a Z-overshoot (mid legs run past the target's near
  // edge) or a taken-side-rotated mixed L can pass straight through the src/tgt
  // body, which renders as a pipe folded over its own device. When that happens,
  // re-route with the same collision-aware machinery the forced-side path uses
  // (candidate ladder → perimeter detour), holding the auto-picked sides fixed
  // so port handles don't churn across diagrams. Non-folding routes (the common
  // case) return the naive waypoints verbatim, so clean diagrams are unchanged.
  // Self-box-only: the router sees just these two boxes; folds over a third
  // device are out of scope. Guaranteed only in the unscaled model (symbolScale
  // === 1) and for non-overlapping boxes (overlap is best-effort).
  const naivePoly = [srcHandle, ...waypoints, tgtHandle]
  if (!pathClear(naivePoly, src, tgt)) {
    return {
      points: routeSidesAvoidingBoxes(src, tgt, srcHandle, tgtHandle, srcSide, tgtSide),
      sourceHandle,
      targetHandle,
    }
  }

  return { points: waypoints, sourceHandle, targetHandle }
}

function handleEdgePosition(box: NodeBox, side: 'right' | 'left' | 'bottom' | 'top'): Point {
  switch (side) {
    case 'right':  return { x: box.center.x + box.halfWidth, y: box.center.y }
    case 'left':   return { x: box.center.x - box.halfWidth, y: box.center.y }
    case 'bottom': return { x: box.center.x, y: box.center.y + box.halfHeight }
    case 'top':    return { x: box.center.x, y: box.center.y - box.halfHeight }
  }
}

function exitPoint(handlePos: Point, side: 'right' | 'left' | 'bottom' | 'top'): Point {
  switch (side) {
    case 'right':  return { x: handlePos.x + MARGIN, y: handlePos.y }
    case 'left':   return { x: handlePos.x - MARGIN, y: handlePos.y }
    case 'bottom': return { x: handlePos.x, y: handlePos.y + MARGIN }
    case 'top':    return { x: handlePos.x, y: handlePos.y - MARGIN }
  }
}

function oppositeSide(side: 'right' | 'left' | 'bottom' | 'top'): 'right' | 'left' | 'bottom' | 'top' {
  switch (side) {
    case 'right':  return 'left'
    case 'left':   return 'right'
    case 'bottom': return 'top'
    case 'top':    return 'bottom'
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

function boxBounds(b: NodeBox): { left: number; right: number; top: number; bottom: number } {
  return {
    left: b.center.x - b.halfWidth,
    right: b.center.x + b.halfWidth,
    top: b.center.y - b.halfHeight,
    bottom: b.center.y + b.halfHeight,
  }
}

/** Distance from `handle` to `other`'s near edge along `side`; ∞ if `other` is not in that direction. */
function clearGapAlong(handle: Point, side: Side, other: NodeBox): number {
  const o = boxBounds(other)
  switch (side) {
    case 'right':  return o.right > handle.x && o.top <= handle.y && handle.y <= o.bottom ? o.left - handle.x : Infinity
    case 'left':   return o.left < handle.x && o.top <= handle.y && handle.y <= o.bottom ? handle.x - o.right : Infinity
    case 'bottom': return o.bottom > handle.y && o.left <= handle.x && handle.x <= o.right ? o.top - handle.y : Infinity
    case 'top':    return o.top < handle.y && o.left <= handle.x && handle.x <= o.right ? handle.y - o.bottom : Infinity
  }
}

function movePoint(p: Point, side: Side, dist: number): Point {
  switch (side) {
    case 'right':  return { x: p.x + dist, y: p.y }
    case 'left':   return { x: p.x - dist, y: p.y }
    case 'bottom': return { x: p.x, y: p.y + dist }
    case 'top':    return { x: p.x, y: p.y - dist }
  }
}

function clampedApproach(handle: Point, side: Side, other: NodeBox): Point {
  const L = clamp(clearGapAlong(handle, side, other) - CLEAR, CLEAR, MARGIN)
  return movePoint(handle, side, L)
}

function segCrosses(p1: Point, p2: Point, b: NodeBox): boolean {
  const bx = boxBounds(b)
  if (p1.y === p2.y) {
    const y = p1.y, lo = Math.min(p1.x, p2.x), hi = Math.max(p1.x, p2.x)
    return bx.top < y && y < bx.bottom && lo < bx.right && hi > bx.left
  }
  const x = p1.x, lo = Math.min(p1.y, p2.y), hi = Math.max(p1.y, p2.y)
  return bx.left < x && x < bx.right && lo < bx.bottom && hi > bx.top
}

function pathClear(poly: Point[], src: NodeBox, tgt: NodeBox): boolean {
  for (let i = 0; i < poly.length - 1; i++) {
    if (segCrosses(poly[i]!, poly[i + 1]!, src)) return false
    if (segCrosses(poly[i]!, poly[i + 1]!, tgt)) return false
  }
  return true
}

/** Candidate connectors (interior points only) between two approach points, in priority order. */
function connectorCandidates(a: Point, b: Point): Point[][] {
  const out: Point[][] = []
  if (a.x === b.x || a.y === b.y) out.push([]) // direct
  out.push([{ x: b.x, y: a.y }]) // L-h
  out.push([{ x: a.x, y: b.y }]) // L-v
  const midX = (a.x + b.x) / 2
  out.push([{ x: midX, y: a.y }, { x: midX, y: b.y }]) // Z-h
  const midY = (a.y + b.y) / 2
  out.push([{ x: a.x, y: midY }, { x: b.x, y: midY }]) // Z-v
  return out
}

/** Perimeter detour around the combined MARGIN-inflated bbox. */
function perimeterDetour(a: Point, b: Point, srcSide: Side, src: NodeBox, tgt: NodeBox): Point[] {
  const s = boxBounds(src), t = boxBounds(tgt)
  const cb = {
    left: Math.min(s.left, t.left) - MARGIN,
    right: Math.max(s.right, t.right) + MARGIN,
    top: Math.min(s.top, t.top) - MARGIN,
    bottom: Math.max(s.bottom, t.bottom) + MARGIN,
  }
  const firstMoveHorizontal = srcSide === 'top' || srcSide === 'bottom'
  if (firstMoveHorizontal) {
    const dir = Math.sign(b.x - a.x) || 1 // tie → +x (right)
    const E = dir > 0 ? cb.right : cb.left
    return [{ x: E, y: a.y }, { x: E, y: b.y }]
  }
  const dir = Math.sign(b.y - a.y) || 1 // tie → +y (down)
  const E = dir > 0 ? cb.bottom : cb.top
  return [{ x: a.x, y: E }, { x: b.x, y: E }] // collapseCollinear drops the degenerate point when a.x === b.x
}

/** Collapse exact duplicates and collinear runs (handles overshoot + zero-length). */
function collapseCollinear(pts: Point[]): Point[] {
  const out: Point[] = []
  for (let i = 0; i < pts.length; i++) {
    const cur = pts[i]!
    const prev = out[out.length - 1]
    const next = pts[i + 1]
    if (prev && prev.x === cur.x && prev.y === cur.y) continue
    if (prev && next && ((prev.x === cur.x && cur.x === next.x) || (prev.y === cur.y && cur.y === next.y))) continue
    out.push(cur)
  }
  return out
}

/**
 * Collision-aware route for a FIXED pair of sides: candidate ladder ranked by
 * pathClear, perimeter-detour fallback. Returns intermediate waypoints (excludes
 * handle endpoints). Used both by the forced/pinned path and by the #502
 * anti-fold guard (which reuses it with auto-picked sides). The sides are inputs
 * either way — this function never picks them.
 */
function routeSidesAvoidingBoxes(
  src: NodeBox, tgt: NodeBox, srcHandle: Point, tgtHandle: Point, srcSide: Side, tgtSide: Side,
): Point[] {
  const srcApproach = clampedApproach(srcHandle, srcSide, tgt)
  const tgtApproach = clampedApproach(tgtHandle, tgtSide, src)
  let mid: Point[] | null = null
  for (const cand of connectorCandidates(srcApproach, tgtApproach)) {
    const full = [srcHandle, srcApproach, ...cand, tgtApproach, tgtHandle]
    if (pathClear(full, src, tgt)) { mid = cand; break }
  }
  if (mid === null) mid = perimeterDetour(srcApproach, tgtApproach, srcSide, src, tgt)
  const full = [srcHandle, srcApproach, ...mid, tgtApproach, tgtHandle]
  return collapseCollinear(full).slice(1, -1) // drop handle endpoints
}

// ============================================================
// Waypoint routing (Phase 2)
// ============================================================

const SIDE_ORDER: Side[] = ['right', 'bottom', 'left', 'top']

/**
 * Stub for a waypoint leg: clampedApproach, additionally clamped by the
 * distance to the adjacent chain corner along the side axis when that corner
 * lies strictly AHEAD (behind/zero → ∞, matching clearGapAlong's convention).
 * Without this, a chain corner inside the 30px margin forces a built-in
 * backtrack spike (leg goes 30 out, then back to a closer corner).
 *
 * Accepted limitation: when 0 < distAhead < CLEAR (a chain corner less than
 * 1px ahead of the handle), the CLEAR floor lets the stub overshoot the corner
 * by up to ~1px. Sub-pixel; no action.
 */
function chainClampedApproach(handle: Point, side: Side, other: NodeBox, chainCorner: Point): Point {
  const gap = clearGapAlong(handle, side, other)
  let distAhead = Infinity
  switch (side) {
    case 'right':  distAhead = chainCorner.x > handle.x ? chainCorner.x - handle.x : Infinity; break
    case 'left':   distAhead = chainCorner.x < handle.x ? handle.x - chainCorner.x : Infinity; break
    case 'bottom': distAhead = chainCorner.y > handle.y ? chainCorner.y - handle.y : Infinity; break
    case 'top':    distAhead = chainCorner.y < handle.y ? handle.y - chainCorner.y : Infinity; break
  }
  const L = clamp(Math.min(gap - CLEAR, distAhead), CLEAR, MARGIN)
  return movePoint(handle, side, L)
}

/**
 * Route one end leg stub→corner: candidate ladder ranked by pathClear
 * against BOTH node boxes; single-box detour fallback. Returns interior
 * corners only (excludes stub and corner). Best-effort — never throws.
 */
function routeLeg(stub: Point, corner: Point, side: Side, own: NodeBox, other: NodeBox): Point[] {
  for (const cand of connectorCandidates(stub, corner)) {
    if (pathClear([stub, ...cand, corner], own, other)) return cand
  }
  return singleBoxDetour(stub, corner, side, own, other)
}

/**
 * Last-resort leg fallback: detour around the MARGIN-inflated box of the
 * leg's OWN node only (the existing perimeterDetour assumes both endpoints
 * hug the combined two-node bbox — wrong obstacle model for a free chain
 * corner). Both rotation directions tried, ranked by pathClear against both
 * boxes; chain corner inside the inflated box → least-bad plain candidate.
 */
function singleBoxDetour(stub: Point, corner: Point, side: Side, own: NodeBox, other: NodeBox): Point[] {
  const b = boxBounds(own)
  const inf = { left: b.left - MARGIN, right: b.right + MARGIN, top: b.top - MARGIN, bottom: b.bottom + MARGIN }
  const inside = corner.x > inf.left && corner.x < inf.right && corner.y > inf.top && corner.y < inf.bottom
  if (!inside) {
    const horizontalFirst = side === 'top' || side === 'bottom'
    const detours: Point[][] = horizontalFirst
      ? [[{ x: inf.right, y: stub.y }, { x: inf.right, y: corner.y }], [{ x: inf.left, y: stub.y }, { x: inf.left, y: corner.y }]]
      : [[{ x: stub.x, y: inf.bottom }, { x: corner.x, y: inf.bottom }], [{ x: stub.x, y: inf.top }, { x: corner.x, y: inf.top }]]
    for (const cand of detours) {
      if (pathClear([stub, ...cand, corner], own, other)) return cand
    }
  }
  return connectorCandidates(stub, corner)[0]!
}

/**
 * Cost-based side pick for an unforced waypoint-leg end: trial all four
 * sides, choose (fewest leg corners, shortest Manhattan length handle→corner
 * including the stub, prefer non-taken, stable SIDE_ORDER). Non-circular —
 * trials need only the NodeBox. Documented divergence: taken sides are a
 * tie-break here, whereas pickNonCollidingSide avoids them outright — a
 * waypointed edge may share a side when it clearly wins on cost (shape
 * fidelity beats dodging).
 */
function pickLegSide(box: NodeBox, other: NodeBox, corner: Point, taken: ReadonlySet<Side> | undefined): Side {
  let best: { side: Side; corners: number; length: number; isTaken: boolean; order: number } | null = null
  for (let i = 0; i < SIDE_ORDER.length; i++) {
    const side = SIDE_ORDER[i]!
    const handle = handleEdgePosition(box, side)
    const stub = chainClampedApproach(handle, side, other, corner)
    const leg = routeLeg(stub, corner, side, box, other)
    const collapsed = collapseCollinear([handle, stub, ...leg, corner])
    let length = 0
    for (let j = 0; j < collapsed.length - 1; j++) {
      length += Math.abs(collapsed[j + 1]!.x - collapsed[j]!.x) + Math.abs(collapsed[j + 1]!.y - collapsed[j]!.y)
    }
    const cand = { side, corners: collapsed.length - 2, length, isTaken: taken?.has(side) ?? false, order: i }
    const better = best === null
      || cand.corners < best.corners
      || (cand.corners === best.corners && cand.length < best.length)
      || (cand.corners === best.corners && cand.length === best.length && !cand.isTaken && best.isTaken)
      || (cand.corners === best.corners && cand.length === best.length && cand.isTaken === best.isTaken && cand.order < best.order)
    if (better) best = cand
  }
  return best!.side
}

/** Route through a user waypoint chain: chain verbatim, adaptive end legs. */
function routeThroughWaypoints(src: NodeBox, tgt: NodeBox, chain: Point[], options?: RouteOptions): RouteResult {
  const wpFirst = chain[0]!
  const wpLast = chain[chain.length - 1]!
  const srcSide = options?.forcedSrcSide ?? pickLegSide(src, tgt, wpFirst, options?.takenSrcSides)
  const tgtSide = options?.forcedTgtSide ?? pickLegSide(tgt, src, wpLast, options?.takenTgtSides)
  const srcHandle = handleEdgePosition(src, srcSide)
  const tgtHandle = handleEdgePosition(tgt, tgtSide)
  const srcStub = chainClampedApproach(srcHandle, srcSide, tgt, wpFirst)
  const tgtStub = chainClampedApproach(tgtHandle, tgtSide, src, wpLast)
  const srcLeg = routeLeg(srcStub, wpFirst, srcSide, src, tgt)
  const tgtLeg = routeLeg(tgtStub, wpLast, tgtSide, tgt, src) // stub-outward; reversed below
  // Junction-sentinel collapse: each window includes its handle AND its
  // adjacent chain end corner as endpoints; slice(1,-1) strips one
  // handle-valued and one sentinel-valued point per window, so `points`
  // excludes handles (forced-path convention; canvas re-adds ports) and
  // contains each chain corner exactly once, verbatim and contiguous.
  // Chain corners never enter a collapse window interior → never removed.
  //
  // The slice(1,-1) is load-bearing for the OPPOSITE reason to the rev-2
  // framing: its job is to hand collapseCollinear the handle + chain-end
  // corner as CONTEXT so it can drop the handle-valued and stub/sentinel-valued
  // endpoints WITHOUT eating a genuine interior leg corner — NOT to remove a
  // leg corner that duplicates wp0. That duplicate is structurally UNREACHABLE
  // for a clear leg: an L-elbow == wp0 needs (wp0.x, stub.y)==wp0 ⇒ stub.y==wp0.y,
  // which is precisely the collinear case where connectorCandidates emits the
  // direct [] candidate FIRST (empty leg, no elbow). So the only "duplicate" the
  // window collapse handles is the stub/sentinel endpoint coincidence, not an
  // interior chain-corner duplicate.
  const srcWindow = collapseCollinear([srcHandle, srcStub, ...srcLeg, wpFirst]).slice(1, -1)
  const tgtWindow = collapseCollinear([wpLast, ...[...tgtLeg].reverse(), tgtStub, tgtHandle]).slice(1, -1)
  return {
    points: [...srcWindow, ...chain, ...tgtWindow],
    sourceHandle: `${srcSide}-src`,
    targetHandle: `${tgtSide}-tgt`,
  }
}
